import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Clock,
  Users,
  MapPin,
  CheckCircle,
  User,
  Calendar as CalendarIcon,
  LayoutGrid,
  List,
  Star
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from '../contexts/BusinessContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { db } from '../config/firebase';
import {
  collection,
  doc,
  updateDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  getDoc,
  addDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import { ProfilePhoto } from '../components/ui/ProfilePhoto';

interface ClassType {
  id: string;
  name: string;
  image?: string;
  duration: number;
  description?: string;
}

interface WaitlistEntry {
  userId: string;
  joinedAt: Date;
}

interface Room {
  id: string;
  name: string;
  capacity: number;
  color: string;
}

interface ScheduledClass {
  id: string;
  name: string;
  instructor: string;
  instructorId: string;
  date: string;
  time: string;
  duration: number;
  maxCapacity: number;
  enrolled: { [userId: string]: boolean };
  waitlist?: WaitlistEntry[];
  classType: string;
  description?: string;
  location: string;
  roomId?: string;
  isActive: boolean;
  isPublished?: boolean; // true = visible para usuarios, false = borrador (admin)
  businessId: string;
}

// Interfaz para membresía del usuario
interface UserMembership {
  id: string;
  membershipId: string;
  userId: string;
  businessId: string;
  timeRestriction: 'all_day' | 'morning' | 'afternoon';
  classRestriction: 'all' | 'specific';
  allowedClassTypeIds?: string[];
  reservationsRemaining: number;
  reservationsUsed?: number;
  isActive: boolean;
}

type ViewMode = 'day' | 'week';

const CalendarPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { businessId, currentBusiness } = useBusiness();
  const [classes, setClasses] = useState<ScheduledClass[]>([]);
  const [allWeekClasses, setAllWeekClasses] = useState<ScheduledClass[]>([]);
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState<ScheduledClass | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [userNames, setUserNames] = useState<{ [userId: string]: { name: string; photoUrl?: string } }>({});
  const [showProfilePhoto, setShowProfilePhoto] = useState<{ name: string; photoUrl: string } | null>(null);
  const [userMembership, setUserMembership] = useState<UserMembership | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('day');

  // Estados para el sistema de valoración
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingClass, setRatingClass] = useState<ScheduledClass | null>(null);
  const [classRating, setClassRating] = useState(0);
  const [instructorRating, setInstructorRating] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [userRatings, setUserRatings] = useState<{ [classId: string]: boolean }>({});

  // Estados para selector de crédito (bono vs membresía)
  const [showCreditSelector, setShowCreditSelector] = useState(false);
  const [pendingEnrollClass, setPendingEnrollClass] = useState<string | null>(null);
  const [selectedCreditType, setSelectedCreditType] = useState<'membership' | 'pass' | null>(null);

  // Ref y estados para scroll asistido de salas
  const roomsScrollRef = useRef<HTMLDivElement>(null);
  const [activeRoomIndex, setActiveRoomIndex] = useState(0);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Obtener política de cancelación del negocio
  const cancellationHours = (currentBusiness as any)?.cancellationPolicy?.hoursBeforeClass ?? 2;
  const maxReservationsPerDay = (currentBusiness as any)?.cancellationPolicy?.maxReservationsPerDay ?? 1;

  // Función para verificar si el usuario puede reservar una clase
  const canUserBookClass = (cls: ScheduledClass): { canBook: boolean; reason?: string } => {
    // Si es admin, puede reservar cualquier cosa
    if (currentUser?.role === 'admin' || currentUser?.role === 'super_admin') {
      return { canBook: true };
    }

    // Verificar si tiene membresía activa O bono activo
    const hasMembership = userMembership?.isActive && (userMembership?.reservationsRemaining ?? 0) > 0;
    const hasPass = currentUser?.activePass && currentUser.activePass.creditsRemaining > 0;

    if (!hasMembership && !hasPass) {
      return { canBook: false, reason: 'No tienes una membresía o bono activo para reservar clases.' };
    }

    // Si tiene membresía, verificar restricciones de membresía
    if (hasMembership && userMembership) {
      // Verificar restricción horaria
      const classHour = parseInt(cls.time.split(':')[0]);
      if (userMembership.timeRestriction === 'morning' && classHour >= 14) {
        // Si también tiene bono, verificar si el bono lo permite
        if (!hasPass) {
          return { canBook: false, reason: 'Tu membresía solo permite reservar clases de mañana (hasta las 14:00).' };
        }
        // Si tiene bono, usará el bono en lugar de la membresía
      } else if (userMembership.timeRestriction === 'afternoon' && classHour < 14) {
        if (!hasPass) {
          return { canBook: false, reason: 'Tu membresía solo permite reservar clases de tarde (desde las 14:00).' };
        }
      }

      // Verificar restricción de tipo de clase
      if (userMembership.classRestriction === 'specific' && userMembership.allowedClassTypeIds) {
        if (!userMembership.allowedClassTypeIds.includes(cls.classType)) {
          // Si también tiene bono, verificar si el bono lo permite
          if (!hasPass) {
            return { canBook: false, reason: 'Tu membresía no incluye este tipo de clase.' };
          }
        }
      }
    }

    // Si tiene bono pero no membresía, verificar restricciones del bono
    if (hasPass && !hasMembership && currentUser?.activePass) {
      const pass = currentUser.activePass;

      console.log('🎟️ Verificando restricciones del bono:', {
        passName: pass.passName,
        timeSlotRestriction: pass.timeSlotRestriction,
        classRestriction: pass.classRestriction,
        classTypeRestrictions: pass.classTypeRestrictions,
        classType: cls.classType,
        classTypeName: cls.name
      });

      // Verificar restricción horaria del bono
      const timeRestriction = pass.timeSlotRestriction;
      if (timeRestriction && timeRestriction !== 'all_day') {
        const classHour = parseInt(cls.time.split(':')[0]);
        console.log('🕐 Verificando horario - Hora clase:', classHour, 'Restricción:', timeRestriction);
        if (timeRestriction === 'morning' && classHour >= 14) {
          return { canBook: false, reason: 'Tu bono solo permite reservar clases de mañana (hasta las 14:00).' };
        } else if (timeRestriction === 'afternoon' && classHour < 14) {
          return { canBook: false, reason: 'Tu bono solo permite reservar clases de tarde (desde las 14:00).' };
        }
      }

      // Verificar restricción de tipo de clase del bono
      // Solo aplicar si classRestriction es 'specific' o si hay IDs definidos
      const isSpecificRestriction = pass.classRestriction === 'specific';
      const classRestrictions = pass.classTypeRestrictions;
      console.log('🎟️ Restricción específica:', isSpecificRestriction, 'IDs permitidos:', classRestrictions, 'ID de esta clase:', cls.classType);

      if (isSpecificRestriction && classRestrictions && classRestrictions.length > 0) {
        if (!classRestrictions.includes(cls.classType)) {
          console.log('🚫 Clase NO permitida por el bono');
          return { canBook: false, reason: 'Tu bono no incluye este tipo de clase.' };
        }
        console.log('✅ Clase permitida por el bono');
      }
    }

    // Si tiene membresía Y bono, verificar si alguno permite la clase
    if (hasMembership && hasPass && currentUser?.activePass) {
      const pass = currentUser.activePass;
      const classHour = parseInt(cls.time.split(':')[0]);

      // Verificar si membresía permite
      let membershipAllows = true;
      if (userMembership?.timeRestriction === 'morning' && classHour >= 14) membershipAllows = false;
      if (userMembership?.timeRestriction === 'afternoon' && classHour < 14) membershipAllows = false;
      if (userMembership?.classRestriction === 'specific' && userMembership.allowedClassTypeIds) {
        if (!userMembership.allowedClassTypeIds.includes(cls.classType)) membershipAllows = false;
      }

      // Verificar si bono permite
      let passAllows = true;
      if (pass.timeSlotRestriction === 'morning' && classHour >= 14) passAllows = false;
      if (pass.timeSlotRestriction === 'afternoon' && classHour < 14) passAllows = false;
      if (pass.classRestriction === 'specific' && pass.classTypeRestrictions && pass.classTypeRestrictions.length > 0) {
        if (!pass.classTypeRestrictions.includes(cls.classType)) passAllows = false;
      }

      // Si ninguno permite, rechazar
      if (!membershipAllows && !passAllows) {
        return { canBook: false, reason: 'Ni tu membresía ni tu bono permiten reservar este tipo de clase.' };
      }
    }

    return { canBook: true };
  };

  // Verificar si una clase está dentro del tiempo de penalización por cancelación
  const isWithinCancellationPenalty = (cls: ScheduledClass) => {
    const classDateTime = new Date(cls.date + 'T' + cls.time);
    const now = new Date();
    const hoursUntilClass = (classDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntilClass < cancellationHours && hoursUntilClass > 0;
  };

  // Obtener horas restantes hasta la clase
  const getHoursUntilClass = (cls: ScheduledClass) => {
    const classDateTime = new Date(cls.date + 'T' + cls.time);
    const now = new Date();
    return Math.max(0, (classDateTime.getTime() - now.getTime()) / (1000 * 60 * 60));
  };

  // Verificar si una clase ya ha pasado (hora de inicio ya pasó)
  const isClassPast = (cls: ScheduledClass) => {
    const classDateTime = new Date(cls.date + 'T' + cls.time);
    const now = new Date();
    return classDateTime.getTime() < now.getTime();
  };

  // Format date for display
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Formatear fecha en formato local YYYY-MM-DD (sin conversión a UTC)
  const formatDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Obtener las salas que tienen clases para mostrar
  const visibleRooms = useMemo(() => {
    const roomsWithClasses = rooms.filter(room =>
      classes.some(cls => cls.roomId === room.id)
    );
    // Añadir "sin sala" si hay clases sin sala asignada
    const hasUnassignedClasses = classes.some(cls => !cls.roomId || !rooms.find(r => r.id === cls.roomId));
    if (hasUnassignedClasses) {
      return [...roomsWithClasses, { id: 'unassigned', name: 'Sin sala', capacity: 0, color: '#6B7280' }];
    }
    return roomsWithClasses;
  }, [rooms, classes]);

  // Funciones para scroll asistido de salas
  const scrollToRoom = useCallback((index: number) => {
    if (!roomsScrollRef.current) return;
    const container = roomsScrollRef.current;
    const roomWidth = 320 + 24; // width + gap
    const targetScroll = index * roomWidth;

    isScrollingRef.current = true;
    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });

    setActiveRoomIndex(index);

    // Resetear flag después de la animación
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 400);
  }, []);

  // Manejar el scroll con snap asistido
  const handleRoomsScroll = useCallback(() => {
    if (!roomsScrollRef.current || isScrollingRef.current) return;

    // Limpiar timeout anterior
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Esperar a que el usuario termine de hacer scroll
    scrollTimeoutRef.current = setTimeout(() => {
      if (!roomsScrollRef.current) return;

      const container = roomsScrollRef.current;
      const roomWidth = 320 + 24; // width + gap
      const scrollLeft = container.scrollLeft;

      // Calcular el índice de sala más cercano
      const nearestIndex = Math.round(scrollLeft / roomWidth);
      const clampedIndex = Math.max(0, Math.min(nearestIndex, visibleRooms.length - 1));

      // Hacer snap a esa sala
      if (clampedIndex !== activeRoomIndex || Math.abs(scrollLeft - clampedIndex * roomWidth) > 10) {
        scrollToRoom(clampedIndex);
      }
    }, 80); // Tiempo corto para que sea responsivo
  }, [activeRoomIndex, visibleRooms.length, scrollToRoom]);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Funciones para vista semanal
  const getWeekDays = (date: Date) => {
    // Crear copia y fijar a mediodía para evitar problemas de zona horaria
    const startOfWeek = new Date(date);
    startOfWeek.setHours(12, 0, 0, 0);

    // Obtener día de la semana (0 = domingo, 1 = lunes, ..., 6 = sábado)
    const day = startOfWeek.getDay();

    // Calcular cuántos días retroceder para llegar al lunes
    // Si es domingo (0), retroceder 6 días; si no, retroceder (day - 1) días
    const daysToMonday = day === 0 ? 6 : day - 1;
    startOfWeek.setDate(startOfWeek.getDate() - daysToMonday);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      days.push(d);
    }
    return days;
  };

  const weekDays = useMemo(() => getWeekDays(selectedDate), [selectedDate]);

  const goToPreviousWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() - 7);
    setSelectedDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + 7);
    setSelectedDate(newDate);
  };

  const formatWeekRange = (days: Date[]) => {
    const start = days[0];
    const end = days[6];
    const startMonth = start.toLocaleDateString('es-ES', { month: 'short' });
    const endMonth = end.toLocaleDateString('es-ES', { month: 'short' });

    if (startMonth === endMonth) {
      return `${start.getDate()} - ${end.getDate()} ${startMonth}`;
    }
    return `${start.getDate()} ${startMonth} - ${end.getDate()} ${endMonth}`;
  };

  const getClassesForDay = (date: Date) => {
    const dateKey = formatDateKey(date);
    return allWeekClasses.filter(cls => cls.date === dateKey);
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return formatDateKey(date1) === formatDateKey(date2);
  };

  const loadProfilePhotos = async (userIds: string[]) => {
    // OPTIMIZACIÓN: Filtrar usuarios que ya tenemos en caché
    const uncachedUserIds = userIds.filter(userId =>
      userId !== currentUser?.id && !userNames[userId]
    );

    const newUserNames: { [userId: string]: { name: string; photoUrl?: string } } = {};

    // Añadir datos del usuario actual si está en la lista
    if (currentUser && userIds.includes(currentUser.id)) {
      newUserNames[currentUser.id] = {
        name: `${currentUser.firstName} ${currentUser.lastName}`,
        photoUrl: currentUser.profilePhotoUrl
      };
    }

    // OPTIMIZACIÓN: Cargar usuarios en batch (máximo 30 por query "in" de Firestore)
    if (uncachedUserIds.length > 0) {
      try {
        const chunks = [];
        for (let i = 0; i < uncachedUserIds.length; i += 30) {
          chunks.push(uncachedUserIds.slice(i, i + 30));
        }

        for (const chunk of chunks) {
          const usersQuery = query(
            collection(db, 'users'),
            where('__name__', 'in', chunk)
          );
          const usersSnapshot = await getDocs(usersQuery);
          usersSnapshot.docs.forEach(docSnap => {
            const userData = docSnap.data();
            newUserNames[docSnap.id] = {
              name: `${userData.firstName} ${userData.lastName}`,
              photoUrl: userData.profilePhotoUrl || userData.photoUrl
            };
          });
        }

        // Marcar usuarios no encontrados
        uncachedUserIds.forEach(userId => {
          if (!newUserNames[userId]) {
            newUserNames[userId] = { name: `Usuario ${userId.slice(0, 6)}...` };
          }
        });
      } catch (error) {
        console.error('Error loading user profiles in batch:', error);
        // Fallback: marcar todos como no encontrados
        uncachedUserIds.forEach(userId => {
          newUserNames[userId] = { name: `Usuario ${userId.slice(0, 6)}...` };
        });
      }
    }

    // Combinar con datos existentes en caché
    setUserNames(prev => ({ ...prev, ...newUserNames }));
  };

  const loadData = useCallback(async () => {
    if (!businessId) return;

    setLoading(true);
    try {
      const dateKey = formatDateKey(selectedDate);

      // Load scheduled classes for the selected date
      const classesQuery = query(
        collection(db, 'classes'),
        where('businessId', '==', businessId),
        where('isActive', '==', true),
        where('date', '==', dateKey)
      );
      const classesSnapshot = await getDocs(classesQuery);
      const classesData = classesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as ScheduledClass))
        .filter(cls => cls.isPublished !== false) // Solo mostrar clases publicadas
        .sort((a, b) => a.time.localeCompare(b.time));
      setClasses(classesData);

      // Load week classes for weekly view
      const weekStart = getWeekDays(selectedDate)[0];
      const weekEnd = getWeekDays(selectedDate)[6];
      const weekStartKey = formatDateKey(weekStart);
      const weekEndKey = formatDateKey(weekEnd);

      const weekClassesQuery = query(
        collection(db, 'classes'),
        where('businessId', '==', businessId),
        where('isActive', '==', true),
        where('date', '>=', weekStartKey),
        where('date', '<=', weekEndKey)
      );
      const weekClassesSnapshot = await getDocs(weekClassesQuery);
      const weekClassesData = weekClassesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as ScheduledClass))
        .filter(cls => cls.isPublished !== false) // Solo mostrar clases publicadas
        .sort((a, b) => {
          if (a.date !== b.date) return a.date.localeCompare(b.date);
          return a.time.localeCompare(b.time);
        });
      setAllWeekClasses(weekClassesData);

      // Load class types for images
      const classTypesQuery = query(
        collection(db, 'classTypes'),
        where('businessId', '==', businessId)
      );
      const classTypesSnapshot = await getDocs(classTypesQuery);
      setClassTypes(classTypesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ClassType)));

      // Load rooms from business config
      const businessDoc = await getDoc(doc(db, 'businesses', businessId));
      if (businessDoc.exists()) {
        const businessData = businessDoc.data();
        setRooms(businessData.rooms || []);
      }

      // Load user's active membership
      if (currentUser?.id) {
        const userMembershipQuery = query(
          collection(db, 'userMemberships'),
          where('userId', '==', currentUser.id),
          where('businessId', '==', businessId),
          where('isActive', '==', true)
        );
        const membershipSnapshot = await getDocs(userMembershipQuery);
        if (!membershipSnapshot.empty) {
          const membershipDoc = membershipSnapshot.docs[0];
          const membershipData = membershipDoc.data();

          // Cargar los datos de la membresía base para obtener restricciones
          const baseMembershipDoc = await getDoc(doc(db, 'memberships', membershipData.membershipId));
          if (baseMembershipDoc.exists()) {
            const baseMembership = baseMembershipDoc.data();
            setUserMembership({
              id: membershipDoc.id,
              membershipId: membershipData.membershipId,
              userId: membershipData.userId,
              businessId: membershipData.businessId,
              timeRestriction: baseMembership.timeRestriction || 'all_day',
              classRestriction: baseMembership.classRestriction || 'all',
              allowedClassTypeIds: baseMembership.allowedClassTypeIds || [],
              reservationsRemaining: membershipData.reservationsRemaining || 0,
              isActive: membershipData.isActive
            });
          }
        } else {
          setUserMembership(null);
        }
      }

      // Load profile photos for all enrolled users
      const allEnrolledUserIds = new Set<string>();
      classesData.forEach(cls => {
        Object.entries(cls.enrolled).forEach(([userId, isEnrolled]) => {
          if (isEnrolled) {
            allEnrolledUserIds.add(userId);
          }
        });
        // Also load waitlist users
        cls.waitlist?.forEach(entry => {
          allEnrolledUserIds.add(entry.userId);
        });
      });

      if (allEnrolledUserIds.size > 0) {
        await loadProfilePhotos(Array.from(allEnrolledUserIds));
      }

      // Cargar valoraciones del usuario para las clases cargadas
      if (currentUser?.id) {
        const allClassIds = [...classesData.map(c => c.id), ...weekClassesData.map(c => c.id)];
        const uniqueClassIds = [...new Set(allClassIds)];

        if (uniqueClassIds.length > 0) {
          const ratingsQuery = query(
            collection(db, 'ratings'),
            where('userId', '==', currentUser.id),
            where('businessId', '==', businessId)
          );
          const ratingsSnapshot = await getDocs(ratingsQuery);
          const userRatingsMap: { [classId: string]: boolean } = {};
          ratingsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            if (data.classId) {
              userRatingsMap[data.classId] = true;
            }
          });
          setUserRatings(userRatingsMap);
        }
      }

    } catch (error) {
      console.error('Error loading classes:', error);
      toast.error('Error al cargar las clases');
    } finally {
      setLoading(false);
    }
  }, [businessId, selectedDate, currentUser]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getClassTypeImage = (classTypeId: string) => {
    const classType = classTypes.find(ct => ct.id === classTypeId);
    return classType?.image;
  };

  const isUserEnrolled = (classId: string) => {
    if (!currentUser) return false;
    const classData = classes.find(cls => cls.id === classId);
    return classData ? classData.enrolled[currentUser.id] === true : false;
  };

  const getEnrolledCount = (enrolled: { [userId: string]: boolean }) => {
    return Object.values(enrolled).filter(Boolean).length;
  };

  const getEnrolledUsers = (enrolled: { [userId: string]: boolean }) => {
    return Object.keys(enrolled).filter(userId => enrolled[userId]);
  };

  const getAvailableSpots = (cls: ScheduledClass) => {
    return cls.maxCapacity - getEnrolledCount(cls.enrolled);
  };

  const getUserName = (userId: string) => {
    if (userId === currentUser?.id) {
      return `${currentUser.firstName} ${currentUser.lastName}`;
    }
    return userNames[userId]?.name || `Usuario ${userId.slice(0, 6)}...`;
  };

  // Verificar si el usuario puede valorar una clase (asistió y la clase ya pasó)
  const canRateClass = (cls: ScheduledClass): boolean => {
    if (!currentUser) return false;
    // El usuario debe estar inscrito
    if (!cls.enrolled[currentUser.id]) return false;
    // La clase debe haber pasado
    if (!isClassPast(cls)) return false;
    // El usuario no debe haber valorado ya
    if (userRatings[cls.id]) return false;
    return true;
  };

  // Abrir modal de valoración
  const openRatingModal = (cls: ScheduledClass) => {
    setRatingClass(cls);
    setClassRating(0);
    setInstructorRating(0);
    setShowRatingModal(true);
  };

  // Enviar valoración
  const submitRating = async () => {
    if (!currentUser || !ratingClass || !businessId) return;
    if (classRating === 0 || instructorRating === 0) {
      toast.error('Por favor, selecciona una valoración para la clase y el instructor');
      return;
    }

    try {
      setSubmittingRating(true);

      // Guardar la valoración
      await addDoc(collection(db, 'ratings'), {
        businessId,
        userId: currentUser.id,
        classId: ratingClass.id,
        className: ratingClass.name,
        classTypeId: ratingClass.classType,
        instructorId: ratingClass.instructorId,
        classRating,
        instructorRating,
        createdAt: serverTimestamp()
      });

      // Actualizar la media del tipo de clase
      const classTypeRef = doc(db, 'classTypes', ratingClass.classType);
      const classTypeDoc = await getDoc(classTypeRef);
      if (classTypeDoc.exists()) {
        const data = classTypeDoc.data();
        const currentRating = data.rating || 0;
        const totalRatings = data.totalRatings || 0;
        const newTotalRatings = totalRatings + 1;
        const newRating = ((currentRating * totalRatings) + classRating) / newTotalRatings;

        await updateDoc(classTypeRef, {
          rating: newRating,
          totalRatings: newTotalRatings,
          updatedAt: serverTimestamp()
        });
      }

      // Actualizar la media del instructor
      const instructorRef = doc(db, 'instructors', ratingClass.instructorId);
      const instructorDoc = await getDoc(instructorRef);
      if (instructorDoc.exists()) {
        const data = instructorDoc.data();
        const currentRating = data.rating || 0;
        const totalRatings = data.totalRatings || 0;
        const newTotalRatings = totalRatings + 1;
        const newRating = ((currentRating * totalRatings) + instructorRating) / newTotalRatings;

        await updateDoc(instructorRef, {
          rating: newRating,
          totalRatings: newTotalRatings,
          totalClasses: (data.totalClasses || 0) + 1,
          updatedAt: serverTimestamp()
        });
      }

      // Actualizar estado local
      setUserRatings(prev => ({ ...prev, [ratingClass.id]: true }));

      toast.success('¡Gracias por tu valoración!');
      setShowRatingModal(false);
      setRatingClass(null);
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Error al enviar la valoración');
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleClassClick = async (cls: ScheduledClass) => {
    setSelectedClass(cls);
    setShowModal(true);

    // Collect all user IDs (enrolled + waitlist)
    const allUserIds = new Set<string>();
    getEnrolledUsers(cls.enrolled).forEach(id => allUserIds.add(id));
    cls.waitlist?.forEach(entry => allUserIds.add(entry.userId));

    if (allUserIds.size > 0) {
      await loadProfilePhotos(Array.from(allUserIds));
    }
  };

  // Verificar si el usuario está en la lista de espera
  const isInWaitlist = (cls: ScheduledClass) => {
    if (!currentUser || !cls.waitlist) return false;
    return cls.waitlist.some(entry => entry.userId === currentUser.id);
  };

  // Obtener posición en la lista de espera
  const getWaitlistPosition = (cls: ScheduledClass) => {
    if (!currentUser || !cls.waitlist) return -1;
    const index = cls.waitlist.findIndex(entry => entry.userId === currentUser.id);
    return index >= 0 ? index + 1 : -1;
  };

  // Crear notificación para usuario promocionado desde lista de espera
  const createWaitlistPromotionNotification = async (userId: string, className: string, classDate: string, classTime: string) => {
    try {
      console.log('🔔 Creando notificación de promoción de lista de espera para:', userId);
      const notifData = {
        userId,
        businessId,
        type: 'waitlist_promotion', // Tipo específico para que el trigger lo detecte
        title: '🎉 ¡Plaza disponible!',
        message: `Se ha liberado una plaza en ${className} (${classDate} a las ${classTime}). ¡Ya estás inscrito automáticamente!`,
        isRead: false,
        sendPush: true, // Flag para indicar que debe enviar push
        createdAt: serverTimestamp()
      };
      console.log('🔔 Datos de notificación:', notifData);
      const docRef = await addDoc(collection(db, 'notifications'), notifData);
      console.log('🔔 Notificación creada con ID:', docRef.id);
    } catch (error) {
      console.error('🔔 Error creating notification:', error);
    }
  };

  // Contar reservas del usuario en un día específico
  const getUserReservationsForDay = (date: string): number => {
    // Combinar clases del día actual y clases de la semana
    const allClasses = viewMode === 'week' ? allWeekClasses : classes;
    return allClasses.filter(cls =>
      cls.date === date &&
      currentUser &&
      cls.enrolled[currentUser.id]
    ).length;
  };

  // Verificar si membresía permite esta clase
  const membershipAllowsClass = (cls: ScheduledClass): boolean => {
    if (!userMembership?.isActive || (userMembership?.reservationsRemaining ?? 0) <= 0) return false;

    const classHour = parseInt(cls.time.split(':')[0]);
    if (userMembership.timeRestriction === 'morning' && classHour >= 14) return false;
    if (userMembership.timeRestriction === 'afternoon' && classHour < 14) return false;
    if (userMembership.classRestriction === 'specific' && userMembership.allowedClassTypeIds) {
      if (!userMembership.allowedClassTypeIds.includes(cls.classType)) return false;
    }
    return true;
  };

  // Verificar si bono permite esta clase
  const passAllowsClass = (cls: ScheduledClass): boolean => {
    if (!currentUser?.activePass || currentUser.activePass.creditsRemaining <= 0) return false;

    const pass = currentUser.activePass;
    const classHour = parseInt(cls.time.split(':')[0]);

    if (pass.timeSlotRestriction === 'morning' && classHour >= 14) return false;
    if (pass.timeSlotRestriction === 'afternoon' && classHour < 14) return false;
    if (pass.classRestriction === 'specific' && pass.classTypeRestrictions && pass.classTypeRestrictions.length > 0) {
      if (!pass.classTypeRestrictions.includes(cls.classType)) return false;
    }
    return true;
  };

  const enrollInClass = async (classId: string, forceCreditType?: 'membership' | 'pass') => {
    if (!currentUser) return;
    try {
      const classRef = doc(db, 'classes', classId);
      const classDoc = await getDoc(classRef);
      if (!classDoc.exists()) {
        toast.error('Clase no encontrada');
        return;
      }
      const classData = classDoc.data() as ScheduledClass;

      // Verificar si la clase ya ha pasado
      if (isClassPast(classData)) {
        toast.error('Esta clase ya ha pasado. No es posible reservar.');
        return;
      }

      // Verificar límite de reservas por día
      if (maxReservationsPerDay > 0) {
        const currentReservations = getUserReservationsForDay(classData.date);
        if (currentReservations >= maxReservationsPerDay) {
          toast.error(`Has alcanzado el límite de ${maxReservationsPerDay} reserva${maxReservationsPerDay > 1 ? 's' : ''} por día`);
          return;
        }
      }

      // Verificar restricciones de membresía/bono
      const bookingCheck = canUserBookClass(classData);
      if (!bookingCheck.canBook) {
        toast.error(bookingCheck.reason || 'No puedes reservar esta clase');
        return;
      }

      const enrolledCount = getEnrolledCount(classData.enrolled);

      // Ya inscrito
      if (classData.enrolled[currentUser.id]) {
        toast.error('Ya estás inscrito');
        return;
      }

      // Ya en lista de espera
      if (isInWaitlist(classData)) {
        toast.error('Ya estás en la lista de espera');
        return;
      }

      // Clase llena - añadir a lista de espera
      if (enrolledCount >= classData.maxCapacity) {
        const waitlistEntry = {
          userId: currentUser.id,
          joinedAt: new Date().toISOString()
        };

        await updateDoc(classRef, {
          waitlist: arrayUnion(waitlistEntry),
          updatedAt: serverTimestamp()
        });

        await loadData();
        setShowModal(false);
        toast.success('Te has unido a la lista de espera. Te avisaremos si se libera una plaza.');
        return;
      }

      // Verificar qué opciones tiene el usuario para esta clase específica
      const canUseMembership = membershipAllowsClass(classData);
      const canUsePass = passAllowsClass(classData);

      console.log('🎟️ Opciones de crédito disponibles:', { canUseMembership, canUsePass, forceCreditType });

      // Si tiene AMBAS opciones y no ha elegido, mostrar selector
      if (canUseMembership && canUsePass && !forceCreditType) {
        setPendingEnrollClass(classId);
        setShowCreditSelector(true);
        return;
      }

      // Determinar qué tipo de crédito usar
      let creditType: 'membership' | 'pass' | null = forceCreditType || null;
      if (!creditType) {
        if (canUseMembership) creditType = 'membership';
        else if (canUsePass) creditType = 'pass';
      }

      if (!creditType) {
        toast.error('No tienes créditos disponibles para esta clase');
        return;
      }

      // Hay plaza disponible - inscribir directamente
      await updateDoc(classRef, {
        [`enrolled.${currentUser.id}`]: true,
        updatedAt: serverTimestamp()
      });

      // Descontar crédito según la elección
      if (creditType === 'membership' && userMembership) {
        // Descontar de membresía
        const newReservationsRemaining = userMembership.reservationsRemaining - 1;
        const newReservationsUsed = (userMembership.reservationsUsed || 0) + 1;

        const userMembershipRef = doc(db, 'userMemberships', userMembership.id);
        await updateDoc(userMembershipRef, {
          reservationsRemaining: newReservationsRemaining,
          reservationsUsed: newReservationsUsed,
          updatedAt: serverTimestamp()
        });

        // También actualizar activeMembership en el usuario
        const userRef = doc(db, 'users', currentUser.id);
        await updateDoc(userRef, {
          'activeMembership.reservationsUsed': newReservationsUsed,
          'activeMembership.reservationsRemaining': newReservationsRemaining,
          updatedAt: serverTimestamp()
        });

        console.log('✅ Crédito descontado de MEMBRESÍA:', newReservationsRemaining, 'restantes');
        toast.success(`¡Reserva confirmada! (Membresía: ${newReservationsRemaining} clases restantes)`);
      } else if (creditType === 'pass' && currentUser.activePass) {
        // Descontar de bono
        const newCreditsRemaining = currentUser.activePass.creditsRemaining - 1;
        const userRef = doc(db, 'users', currentUser.id);
        await updateDoc(userRef, {
          'activePass.creditsRemaining': newCreditsRemaining,
          updatedAt: serverTimestamp()
        });

        console.log('✅ Crédito descontado de BONO:', newCreditsRemaining, 'restantes');
        toast.success(`¡Reserva confirmada! (Bono: ${newCreditsRemaining} clases restantes)`);
      }

      await loadData();
      setShowModal(false);
      setShowCreditSelector(false);
      setPendingEnrollClass(null);
    } catch (error) {
      console.error('Error enrolling:', error);
      toast.error('Error al reservar');
    }
  };

  // Handler para confirmar selección de crédito
  const handleCreditSelection = async (creditType: 'membership' | 'pass') => {
    if (!pendingEnrollClass) return;
    setShowCreditSelector(false);
    await enrollInClass(pendingEnrollClass, creditType);
    setPendingEnrollClass(null);
  };

  // Salir de la lista de espera
  const leaveWaitlist = async (classId: string) => {
    if (!currentUser) return;
    try {
      const classRef = doc(db, 'classes', classId);
      const classDoc = await getDoc(classRef);
      if (!classDoc.exists()) {
        toast.error('Clase no encontrada');
        return;
      }
      const classData = classDoc.data() as ScheduledClass;

      // Encontrar la entrada del usuario en la lista de espera
      const userEntry = classData.waitlist?.find(entry => entry.userId === currentUser.id);
      if (!userEntry) {
        toast.error('No estás en la lista de espera');
        return;
      }

      await updateDoc(classRef, {
        waitlist: arrayRemove(userEntry),
        updatedAt: serverTimestamp()
      });

      await loadData();
      setShowModal(false);
      toast.success('Has salido de la lista de espera');
    } catch (error) {
      console.error('Error leaving waitlist:', error);
      toast.error('Error al salir de la lista de espera');
    }
  };

  const cancelEnrollment = async (classId: string) => {
    if (!currentUser || !businessId) return;
    try {
      console.log('🚫 Cancelando inscripción...');
      const classRef = doc(db, 'classes', classId);
      const classDoc = await getDoc(classRef);
      if (!classDoc.exists()) {
        toast.error('Clase no encontrada');
        return;
      }
      const classData = classDoc.data() as ScheduledClass;
      console.log('🚫 Datos de la clase:', classData);
      console.log('🚫 Lista de espera:', classData.waitlist);

      if (!classData.enrolled[currentUser.id]) {
        toast.error('No estás inscrito');
        return;
      }

      // Obtener la política de cancelación del negocio
      const businessDoc = await getDoc(doc(db, 'businesses', businessId));
      const hoursBeforeClass = businessDoc.exists()
        ? (businessDoc.data().cancellationPolicy?.hoursBeforeClass ?? 2)
        : 2;

      // Cancelar la inscripción del usuario actual
      await updateDoc(classRef, {
        [`enrolled.${currentUser.id}`]: false,
        updatedAt: serverTimestamp()
      });
      console.log('🚫 Inscripción cancelada');

      // Calcular si está dentro del tiempo límite para cancelar sin penalización
      const classDateTime = new Date(classData.date + 'T' + classData.time);
      const now = new Date();
      const hoursUntilClass = (classDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
      const canRefund = hoursUntilClass >= hoursBeforeClass;

      // Devolver crédito solo si está fuera del tiempo límite
      // Intentamos devolver a membresía primero, luego a bono
      if (canRefund) {
        const userRef = doc(db, 'users', currentUser.id);

        // Si tiene membresía activa, devolver ahí
        if (userMembership?.isActive) {
          const newReservationsRemaining = (userMembership.reservationsRemaining || 0) + 1;
          const newReservationsUsed = Math.max(0, (userMembership.reservationsUsed || 0) - 1);

          const userMembershipRef = doc(db, 'userMemberships', userMembership.id);
          await updateDoc(userMembershipRef, {
            reservationsRemaining: newReservationsRemaining,
            reservationsUsed: newReservationsUsed,
            updatedAt: serverTimestamp()
          });

          await updateDoc(userRef, {
            'activeMembership.reservationsUsed': newReservationsUsed,
            'activeMembership.reservationsRemaining': newReservationsRemaining,
            updatedAt: serverTimestamp()
          });

          console.log('✅ Crédito devuelto a MEMBRESÍA:', newReservationsRemaining);
        } else if (currentUser.activePass) {
          // Si no tiene membresía, devolver al bono
          await updateDoc(userRef, {
            'activePass.creditsRemaining': currentUser.activePass.creditsRemaining + 1,
            updatedAt: serverTimestamp()
          });

          console.log('✅ Crédito devuelto a BONO:', currentUser.activePass.creditsRemaining + 1);
        }
      }

      // Promover al primer usuario de la lista de espera
      console.log('🚫 Verificando lista de espera...', { waitlist: classData.waitlist, length: classData.waitlist?.length });
      if (classData.waitlist && classData.waitlist.length > 0) {
        console.log('🚫 ¡Hay usuarios en lista de espera! Promoviendo...');
        // Ordenar por fecha de inscripción (el más antiguo primero)
        const sortedWaitlist = [...classData.waitlist].sort((a, b) =>
          new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime()
        );

        const nextUser = sortedWaitlist[0];

        // Inscribir al siguiente usuario
        await updateDoc(classRef, {
          [`enrolled.${nextUser.userId}`]: true,
          waitlist: arrayRemove(nextUser),
          updatedAt: serverTimestamp()
        });

        // NOTA: El descuento de créditos del usuario promocionado se maneja
        // automáticamente cuando él confirme su asistencia o mediante Cloud Functions.
        // No podemos modificar el documento de otro usuario por reglas de seguridad.

        // Crear notificación para el usuario promocionado
        const formattedDate = new Date(classData.date).toLocaleDateString('es-ES', {
          weekday: 'long',
          day: 'numeric',
          month: 'long'
        });
        await createWaitlistPromotionNotification(
          nextUser.userId,
          classData.name,
          formattedDate,
          classData.time
        );

        const hasCredits = (currentUser.activePass && currentUser.activePass.creditsRemaining >= 0) || userMembership?.isActive;
        if (canRefund && hasCredits) {
          toast.success('Reserva cancelada. Tu crédito ha sido devuelto.');
        } else if (!canRefund && hasCredits) {
          toast.error(`Reserva cancelada. Cancelaste con menos de ${hoursBeforeClass}h de antelación, no se devuelve el crédito.`, { duration: 5000 });
        } else {
          toast.success('Reserva cancelada.');
        }
      } else {
        const hasCredits = (currentUser.activePass && currentUser.activePass.creditsRemaining >= 0) || userMembership?.isActive;
        if (canRefund && hasCredits) {
          toast.success('Reserva cancelada. Tu crédito ha sido devuelto.');
        } else if (!canRefund && hasCredits) {
          toast.error(`Reserva cancelada. Cancelaste con menos de ${hoursBeforeClass}h de antelación, no se devuelve el crédito.`, { duration: 5000 });
        } else {
          toast.success('Reserva cancelada.');
        }
      }

      await loadData();
      setShowModal(false);
    } catch (error) {
      console.error('Error canceling:', error);
      toast.error('Error al cancelar');
    }
  };

  const isToday = formatDateKey(selectedDate) === formatDateKey(new Date());
  const isPast = selectedDate < new Date(new Date().setHours(0, 0, 0, 0));

  return (
    <>
      <Helmet>
        <title>Reserva | {currentBusiness?.name || 'Reserberry Sport'}</title>
      </Helmet>

      <div className="min-h-screen bg-gray-950 pb-20">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gray-950/95 backdrop-blur-sm border-b border-gray-800 px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-amber-400">Reserva</h1>
            <div className="flex items-center gap-2">
              {/* View Mode Toggle */}
              <div className="flex bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('day')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'day'
                    ? 'bg-amber-500 text-white'
                    : 'text-gray-400 hover:text-white'
                    }`}
                  title="Vista diaria"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('week')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'week'
                    ? 'bg-amber-500 text-white'
                    : 'text-gray-400 hover:text-white'
                    }`}
                  title="Vista semanal"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={goToToday}
                className={isToday ? 'bg-amber-500/20 border-amber-500' : ''}
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                Hoy
              </Button>
            </div>
          </div>

          {/* Date Navigator - Daily View */}
          {viewMode === 'day' && (
            <div className="flex items-center justify-between bg-gray-900 rounded-xl p-3">
              <button
                onClick={goToPreviousDay}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-gray-400" />
              </button>

              <div className="text-center">
                <p className="text-lg font-semibold text-white capitalize">
                  {formatDate(selectedDate)}
                </p>
                {isToday && (
                  <span className="text-xs text-amber-400 font-medium">Hoy</span>
                )}
              </div>

              <button
                onClick={goToNextDay}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-gray-400" />
              </button>
            </div>
          )}

          {/* Week Navigator - Weekly View */}
          {viewMode === 'week' && (
            <div className="flex items-center justify-between bg-gray-900 rounded-xl p-3">
              <button
                onClick={goToPreviousWeek}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-gray-400" />
              </button>

              <div className="text-center">
                <p className="text-lg font-semibold text-white capitalize">
                  {formatWeekRange(weekDays)}
                </p>
                <span className="text-xs text-gray-400">Semana</span>
              </div>

              <button
                onClick={goToNextWeek}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-gray-400" />
              </button>
            </div>
          )}
        </div>

        {/* Room Tabs - Only show if there are multiple rooms */}
        {rooms.length > 1 && (
          <div className="px-4 pt-4">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => setSelectedRoom('all')}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedRoom === 'all'
                  ? 'bg-amber-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
              >
                Todas las salas
              </button>
              {rooms.map(room => (
                <button
                  key={room.id}
                  onClick={() => setSelectedRoom(room.id)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${selectedRoom === room.id
                    ? 'text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  style={selectedRoom === room.id ? { backgroundColor: room.color } : {}}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: room.color }}
                  />
                  {room.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Classes List */}
        <div className="px-4 py-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
                <p className="text-gray-400">Cargando clases...</p>
              </div>
            </div>
          ) : viewMode === 'week' ? (
            /* Weekly View */
            <div className="overflow-x-auto scrollbar-hide -mx-4">
              <div className="min-w-[900px] px-4">
                {/* Week Header */}
                <div className="grid grid-cols-7 gap-2 mb-4">
                  {weekDays.map((day, index) => {
                    const isCurrentDay = isSameDay(day, new Date());
                    const isSelectedDay = isSameDay(day, selectedDate);
                    const dayClasses = getClassesForDay(day);
                    const dayName = day.toLocaleDateString('es-ES', { weekday: 'short' }).toUpperCase();

                    return (
                      <button
                        key={index}
                        onClick={() => {
                          setSelectedDate(day);
                          setViewMode('day');
                        }}
                        className={`text-center py-2 px-1 rounded-xl transition-all ${isCurrentDay
                          ? 'bg-amber-500 text-white'
                          : isSelectedDay
                            ? 'bg-gray-700 text-white'
                            : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
                          }`}
                      >
                        <p className="text-[10px] font-medium opacity-70">{dayName}</p>
                        <p className={`text-lg font-bold ${isCurrentDay ? 'text-white' : ''}`}>
                          {day.getDate()}
                        </p>
                        {dayClasses.length > 0 && (
                          <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1 ${isCurrentDay ? 'bg-white' : 'bg-amber-400'
                            }`} />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Week Grid */}
                <div className="grid grid-cols-7 gap-2">
                  {weekDays.map((day, dayIndex) => {
                    const dayClasses = getClassesForDay(day);
                    const isCurrentDay = isSameDay(day, new Date());
                    const isPastDay = day < new Date(new Date().setHours(0, 0, 0, 0));

                    // Para el día actual, filtrar clases pasadas y mostrar las próximas primero
                    const sortedDayClasses = isCurrentDay
                      ? dayClasses
                        .filter(cls => !isClassPast(cls)) // Solo clases futuras para hoy
                        .concat(dayClasses.filter(cls => isClassPast(cls))) // Clases pasadas al final
                      : dayClasses;

                    // Para días futuros, mostrar todas; para días pasados, mostrar todas opacas
                    const visibleClasses = isCurrentDay
                      ? sortedDayClasses.filter(cls => !isClassPast(cls)).slice(0, 5) // Solo próximas para hoy
                      : sortedDayClasses.slice(0, 5);

                    const totalUpcoming = isCurrentDay
                      ? dayClasses.filter(cls => !isClassPast(cls)).length
                      : dayClasses.length;

                    return (
                      <div
                        key={dayIndex}
                        className={`min-h-[200px] rounded-xl p-2 ${isCurrentDay
                          ? 'bg-amber-500/10 border border-amber-500/30'
                          : isPastDay
                            ? 'bg-gray-900/30'
                            : 'bg-gray-900/50'
                          }`}
                      >
                        {visibleClasses.length === 0 ? (
                          <div className="h-full flex items-center justify-center">
                            <p className="text-gray-600 text-xs text-center">
                              {isCurrentDay && dayClasses.length > 0 ? 'No hay más clases hoy' : 'Sin clases'}
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {visibleClasses.map((cls) => {
                              const isEnrolled = currentUser && cls.enrolled[currentUser.id];
                              const isFull = getAvailableSpots(cls) <= 0;
                              const isPast = isClassPast(cls);

                              return (
                                <button
                                  key={cls.id}
                                  onClick={() => handleClassClick(cls)}
                                  className={`w-full text-left p-2 rounded-lg transition-all ${isPast
                                    ? 'bg-gray-800/50 opacity-50'
                                    : isEnrolled
                                      ? 'bg-green-600/20 border border-green-500/30'
                                      : isFull
                                        ? 'bg-gray-800/50'
                                        : 'bg-gray-800 hover:bg-gray-700'
                                    }`}
                                >
                                  <p className="text-[10px] text-amber-400 font-medium">
                                    {cls.time}
                                  </p>
                                  <p className="text-xs text-white font-medium truncate">
                                    {cls.name}
                                  </p>
                                  <div className="flex items-center justify-between mt-1">
                                    <span className={`text-[10px] ${isFull ? 'text-red-400' : 'text-gray-500'
                                      }`}>
                                      {getEnrolledCount(cls.enrolled)}/{cls.maxCapacity}
                                    </span>
                                    {isEnrolled && (
                                      <CheckCircle className="w-3 h-3 text-green-400" />
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                            {totalUpcoming > 5 && (
                              <button
                                onClick={() => {
                                  setSelectedDate(day);
                                  setViewMode('day');
                                }}
                                className="w-full text-center py-1 text-xs text-amber-400 hover:text-amber-300"
                              >
                                +{totalUpcoming - 5} más
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Week Summary */}
                <div className="mt-6 bg-gray-900 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-gray-400 mb-3">Resumen de la semana</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-white">{allWeekClasses.length}</p>
                      <p className="text-xs text-gray-500">Clases totales</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-400">
                        {allWeekClasses.filter(cls => currentUser && cls.enrolled[currentUser.id]).length}
                      </p>
                      <p className="text-xs text-gray-500">Reservadas</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-400">
                        {allWeekClasses.filter(cls => getAvailableSpots(cls) > 0 && !isClassPast(cls)).length}
                      </p>
                      <p className="text-xs text-gray-500">Disponibles</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : classes.length === 0 ? (
            <div className="text-center py-20">
              <CalendarIcon className="w-16 h-16 text-gray-700 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">
                No hay clases programadas
              </h3>
              <p className="text-gray-500">
                No hay clases para este día
              </p>
            </div>
          ) : rooms.length > 1 && selectedRoom === 'all' ? (
            /* Multi-room horizontal scroll view with assisted scroll */
            <div className="relative">
              {/* Room indicators */}
              {visibleRooms.length > 1 && (
                <div className="flex justify-center gap-2 mb-4">
                  {visibleRooms.map((room, index) => (
                    <button
                      key={room.id}
                      onClick={() => scrollToRoom(index)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-300 ${activeRoomIndex === index
                        ? 'bg-white/20 scale-105'
                        : 'bg-white/5 hover:bg-white/10'
                        }`}
                    >
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: room.color }}
                      />
                      <span className={`text-xs font-medium transition-colors ${activeRoomIndex === index ? 'text-white' : 'text-gray-400'
                        }`}>
                        {room.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Scroll container with assisted snap */}
              <div
                ref={roomsScrollRef}
                onScroll={handleRoomsScroll}
                className="overflow-x-auto scrollbar-hide -mx-4 px-4 rooms-carousel"
              >
                <div className="flex gap-6" style={{ minWidth: 'max-content' }}>
                  {rooms.map(room => {
                    const roomClasses = classes.filter(cls => cls.roomId === room.id);
                    if (roomClasses.length === 0) return null;

                    return (
                      <div key={room.id} className="flex-shrink-0 snap-center" style={{ width: '320px' }}>
                        {/* Room Header */}
                        <div
                          className="flex items-center gap-2 mb-4 pb-2 border-b-2"
                          style={{ borderColor: room.color }}
                        >
                          <span
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: room.color }}
                          />
                          <h3 className="text-lg font-bold text-white">{room.name}</h3>
                          <span className="text-xs text-gray-500">({roomClasses.length} clases)</span>
                        </div>

                        {/* Room Classes */}
                        <div className="space-y-4">
                          {roomClasses.map((cls) => {
                            const isEnrolled = isUserEnrolled(cls.id);
                            const inWaitlist = isInWaitlist(cls);
                            const waitlistPos = getWaitlistPosition(cls);
                            const isFull = getAvailableSpots(cls) <= 0;
                            const classImage = getClassTypeImage(cls.classType);
                            const isPast = isClassPast(cls);
                            const spotsLeft = getAvailableSpots(cls);

                            return (
                              <div
                                key={cls.id}
                                onClick={() => handleClassClick(cls)}
                                className={`relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 ${isPast
                                  ? 'opacity-50 grayscale cursor-not-allowed'
                                  : 'hover:scale-[1.02] hover:shadow-xl'
                                  } ${isEnrolled ? 'ring-2 ring-green-500' : inWaitlist ? 'ring-2 ring-blue-500' : ''
                                  }`}
                                style={{ minHeight: '160px' }}
                              >
                                {/* Background Image */}
                                <div className="absolute inset-0">
                                  {classImage ? (
                                    <img src={classImage} alt={cls.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${room.color}, ${room.color}99)` }} />
                                  )}
                                  <div className={`absolute inset-0 ${isPast ? 'bg-black/70' : 'bg-gradient-to-t from-black via-black/60 to-transparent'}`} />
                                </div>

                                {/* Content */}
                                <div className="relative h-full p-4 flex flex-col justify-end" style={{ minHeight: '160px' }}>
                                  {/* Status badges */}
                                  <div className="absolute top-3 right-3 flex gap-1">
                                    {isPast && (
                                      <span className="px-2 py-0.5 bg-gray-600 text-gray-300 text-[10px] font-bold rounded-full">
                                        PASADA
                                      </span>
                                    )}
                                    {isEnrolled && !isPast && (
                                      <span className="px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1">
                                        <CheckCircle className="w-2.5 h-2.5" />
                                        INSCRITO
                                      </span>
                                    )}
                                    {inWaitlist && !isPast && (
                                      <span className="px-2 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1">
                                        <Clock className="w-2.5 h-2.5" />
                                        #{waitlistPos}
                                      </span>
                                    )}
                                    {isFull && !isEnrolled && !inWaitlist && !isPast && (
                                      <span className="px-2 py-0.5 bg-red-500/80 text-white text-[10px] font-bold rounded-full">
                                        LLENO
                                      </span>
                                    )}
                                  </div>

                                  {/* Time badge */}
                                  <div className="absolute top-3 left-3">
                                    <span className="px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs font-bold rounded-lg flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {cls.time}
                                    </span>
                                  </div>

                                  {/* Class info */}
                                  <div className="mt-auto">
                                    <h4 className="text-lg font-bold text-white mb-1 drop-shadow-lg">{cls.name}</h4>
                                    <div className="flex flex-wrap items-center gap-2 text-white/80 text-xs">
                                      <span className="flex items-center gap-1">
                                        <User className="w-3 h-3" />
                                        {cls.instructor?.split(' ')[0]}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Users className="w-3 h-3" />
                                        {spotsLeft > 0 ? `${spotsLeft} plazas` : 'Completo'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {/* Classes without room */}
                  {classes.filter(cls => !cls.roomId || !rooms.find(r => r.id === cls.roomId)).length > 0 && (
                    <div className="flex-shrink-0 snap-center" style={{ width: '320px' }}>
                      <div className="flex items-center gap-2 mb-4 pb-2 border-b-2 border-gray-600">
                        <span className="w-3 h-3 rounded-full bg-gray-500" />
                        <h3 className="text-lg font-bold text-white">Sin sala asignada</h3>
                      </div>
                      <div className="space-y-4">
                        {classes.filter(cls => !cls.roomId || !rooms.find(r => r.id === cls.roomId)).map((cls) => {
                          const isEnrolled = isUserEnrolled(cls.id);
                          const inWaitlist = isInWaitlist(cls);
                          const waitlistPos = getWaitlistPosition(cls);
                          const isFull = getAvailableSpots(cls) <= 0;
                          const classImage = getClassTypeImage(cls.classType);
                          const spotsLeft = getAvailableSpots(cls);
                          const isPast = isClassPast(cls);

                          return (
                            <div
                              key={cls.id}
                              onClick={() => handleClassClick(cls)}
                              className={`relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 ${isPast
                                ? 'opacity-50 grayscale cursor-not-allowed'
                                : 'hover:scale-[1.02] hover:shadow-xl'
                                } ${isEnrolled ? 'ring-2 ring-green-500' : inWaitlist ? 'ring-2 ring-blue-500' : ''
                                }`}
                              style={{ minHeight: '160px' }}
                            >
                              <div className="absolute inset-0">
                                {classImage ? (
                                  <img src={classImage} alt={cls.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full bg-gradient-to-br from-gray-600 to-gray-700" />
                                )}
                                <div className={`absolute inset-0 ${isPast ? 'bg-black/70' : 'bg-gradient-to-t from-black via-black/60 to-transparent'}`} />
                              </div>
                              <div className="relative h-full p-4 flex flex-col justify-end" style={{ minHeight: '160px' }}>
                                <div className="absolute top-3 right-3 flex gap-1">
                                  {isPast && (
                                    <span className="px-2 py-0.5 bg-gray-600 text-gray-300 text-[10px] font-bold rounded-full">
                                      PASADA
                                    </span>
                                  )}
                                  {isEnrolled && !isPast && (
                                    <span className="px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1">
                                      <CheckCircle className="w-2.5 h-2.5" /> INSCRITO
                                    </span>
                                  )}
                                  {inWaitlist && !isPast && (
                                    <span className="px-2 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1">
                                      <Clock className="w-2.5 h-2.5" /> #{waitlistPos}
                                    </span>
                                  )}
                                  {isFull && !isEnrolled && !inWaitlist && !isPast && (
                                    <span className="px-2 py-0.5 bg-red-500/80 text-white text-[10px] font-bold rounded-full">LLENO</span>
                                  )}
                                </div>
                                <div className="absolute top-3 left-3">
                                  <span className="px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs font-bold rounded-lg flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> {cls.time}
                                  </span>
                                </div>
                                <div className="mt-auto">
                                  <h4 className={`text-lg font-bold mb-1 ${isPast ? 'text-gray-400' : 'text-white'}`}>{cls.name}</h4>
                                  <div className={`flex flex-wrap items-center gap-2 text-xs ${isPast ? 'text-gray-500' : 'text-white/80'}`}>
                                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{cls.instructor?.split(' ')[0]}</span>
                                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{spotsLeft > 0 ? `${spotsLeft} plazas` : 'Completo'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Dot indicators at bottom */}
              {visibleRooms.length > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  {visibleRooms.map((room, index) => (
                    <button
                      key={room.id}
                      onClick={() => scrollToRoom(index)}
                      className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${activeRoomIndex === index
                        ? 'scale-125'
                        : 'opacity-40 hover:opacity-70'
                        }`}
                      style={{ backgroundColor: room.color }}
                      aria-label={`Ir a ${room.name}`}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Single room or filtered view */
            <div className="space-y-4">
              {(selectedRoom === 'all' ? classes : classes.filter(cls => cls.roomId === selectedRoom)).map((cls) => {
                const isEnrolled = isUserEnrolled(cls.id);
                const inWaitlist = isInWaitlist(cls);
                const waitlistPos = getWaitlistPosition(cls);
                const isFull = getAvailableSpots(cls) <= 0;
                const classImage = getClassTypeImage(cls.classType);
                const spotsLeft = getAvailableSpots(cls);
                const room = rooms.find(r => r.id === cls.roomId);
                const isPast = isClassPast(cls);

                return (
                  <div
                    key={cls.id}
                    onClick={() => handleClassClick(cls)}
                    className={`relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 ${isPast
                      ? 'opacity-50 grayscale cursor-not-allowed'
                      : 'hover:scale-[1.02] hover:shadow-xl'
                      } ${isEnrolled ? 'ring-2 ring-green-500' : inWaitlist ? 'ring-2 ring-blue-500' : ''
                      }`}
                    style={{ minHeight: '180px' }}
                  >
                    {/* Background Image */}
                    <div className="absolute inset-0">
                      {classImage ? (
                        <img
                          src={classImage}
                          alt={cls.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div
                          className="w-full h-full"
                          style={{ background: room ? `linear-gradient(135deg, ${room.color}, ${room.color}99)` : 'linear-gradient(135deg, #d97706, #ea580c)' }}
                        />
                      )}
                      {/* Dark overlay */}
                      <div className={`absolute inset-0 ${isPast ? 'bg-black/70' : 'bg-gradient-to-t from-black via-black/60 to-transparent'}`} />
                    </div>

                    {/* Content */}
                    <div className="relative h-full p-5 flex flex-col justify-end" style={{ minHeight: '180px' }}>
                      {/* Time badge with optional room indicator */}
                      <div className="absolute top-4 left-4 flex items-center gap-2">
                        <span className="px-3 py-1.5 bg-black/60 backdrop-blur-sm text-white text-sm font-bold rounded-lg flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {cls.time}
                        </span>
                        {room && rooms.length > 1 && (
                          <span
                            className="px-2 py-1.5 text-white text-xs font-medium rounded-lg flex items-center gap-1.5"
                            style={{ backgroundColor: `${room.color}CC` }}
                          >
                            {room.name}
                          </span>
                        )}
                      </div>

                      {/* Status badges */}
                      <div className="absolute top-4 right-4 flex gap-2">
                        {isPast && (
                          <span className="px-3 py-1 bg-gray-600 text-gray-300 text-xs font-bold rounded-full">
                            PASADA
                          </span>
                        )}
                        {isEnrolled && !isPast && (
                          <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            INSCRITO
                          </span>
                        )}
                        {inWaitlist && !isPast && (
                          <span className="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            ESPERA #{waitlistPos}
                          </span>
                        )}
                        {isFull && !isEnrolled && !inWaitlist && !isPast && (
                          <span className="px-3 py-1 bg-red-500/80 text-white text-xs font-bold rounded-full">
                            COMPLETO
                          </span>
                        )}
                      </div>

                      {/* Class info */}
                      <div className="mt-auto">
                        <h3 className={`text-2xl font-bold mb-2 drop-shadow-lg ${isPast ? 'text-gray-400' : 'text-white'}`}>
                          {cls.name}
                        </h3>

                        <div className={`flex flex-wrap items-center gap-4 text-sm ${isPast ? 'text-gray-500' : 'text-white/90'}`}>
                          <span className="flex items-center gap-1.5">
                            <User className="w-4 h-4" />
                            {cls.instructor}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4" />
                            {cls.duration} min
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Users className="w-4 h-4" />
                            {spotsLeft > 0 ? `${spotsLeft} plazas` : 'Completo'}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <MapPin className="w-4 h-4" />
                            {cls.location}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Class Detail Modal */}
      {showModal && selectedClass && (
        <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50">
          <div className="w-full sm:max-w-lg bg-gray-900 rounded-t-3xl sm:rounded-2xl max-h-[90vh] overflow-hidden">
            {/* Modal Header with Image */}
            <div className="relative h-48">
              {getClassTypeImage(selectedClass.classType) ? (
                <img
                  src={getClassTypeImage(selectedClass.classType)}
                  alt={selectedClass.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-amber-600 to-orange-700" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />

              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 p-2 bg-black/50 backdrop-blur-sm rounded-full hover:bg-black/70 transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>

              <div className="absolute bottom-4 left-4 right-4">
                <h2 className="text-2xl font-bold text-white drop-shadow-lg">
                  {selectedClass.name}
                </h2>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 12rem)' }}>
              {/* Class Details */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <Clock className="w-4 h-4" />
                    Horario
                  </div>
                  <p className="text-white font-semibold">{selectedClass.time}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <Clock className="w-4 h-4" />
                    Duración
                  </div>
                  <p className="text-white font-semibold">{selectedClass.duration} min</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <User className="w-4 h-4" />
                    Instructor
                  </div>
                  <p className="text-white font-semibold">{selectedClass.instructor}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <MapPin className="w-4 h-4" />
                    Sala
                  </div>
                  <p className="text-white font-semibold">{selectedClass.location}</p>
                </div>
              </div>

              {/* Spots */}
              <div className="bg-gray-800 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Users className="w-5 h-5" />
                    <span>Plazas disponibles</span>
                  </div>
                  <span className={`text-xl font-bold ${getAvailableSpots(selectedClass) <= 0 ? 'text-red-400' :
                    getAvailableSpots(selectedClass) <= 3 ? 'text-amber-400' : 'text-green-400'
                    }`}>
                    {getAvailableSpots(selectedClass)} / {selectedClass.maxCapacity}
                  </span>
                </div>
                {/* Progress bar */}
                <div className="mt-3 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${getAvailableSpots(selectedClass) <= 0 ? 'bg-red-500' :
                      getAvailableSpots(selectedClass) <= 3 ? 'bg-amber-500' : 'bg-green-500'
                      }`}
                    style={{ width: `${(getEnrolledCount(selectedClass.enrolled) / selectedClass.maxCapacity) * 100}%` }}
                  />
                </div>
              </div>

              {/* Enrolled users */}
              {getEnrolledUsers(selectedClass.enrolled).length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-400 mb-3">
                    Participantes ({getEnrolledCount(selectedClass.enrolled)})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {getEnrolledUsers(selectedClass.enrolled).slice(0, 8).map(userId => (
                      <div
                        key={userId}
                        className="flex items-center gap-2 bg-gray-800 rounded-full pl-1 pr-3 py-1 cursor-pointer hover:bg-gray-700 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (userNames[userId]?.photoUrl) {
                            setShowProfilePhoto({
                              name: getUserName(userId),
                              photoUrl: userNames[userId].photoUrl!
                            });
                          }
                        }}
                      >
                        <ProfilePhoto
                          photoUrl={userNames[userId]?.photoUrl}
                          name={getUserName(userId)}
                          size="xs"
                        />
                        <span className="text-xs text-gray-300">
                          {userId === currentUser?.id ? 'Tú' : getUserName(userId).split(' ')[0]}
                        </span>
                      </div>
                    ))}
                    {getEnrolledUsers(selectedClass.enrolled).length > 8 && (
                      <div className="flex items-center justify-center w-8 h-8 bg-gray-800 rounded-full">
                        <span className="text-xs text-gray-400">
                          +{getEnrolledUsers(selectedClass.enrolled).length - 8}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Waitlist info */}
              {selectedClass.waitlist && selectedClass.waitlist.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Lista de espera ({selectedClass.waitlist.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedClass.waitlist.slice(0, 5).map((entry, index) => (
                      <div
                        key={entry.userId}
                        className="flex items-center gap-2 bg-gray-800/50 border border-gray-700 rounded-full pl-1 pr-3 py-1 cursor-pointer hover:bg-gray-700 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (userNames[entry.userId]?.photoUrl) {
                            setShowProfilePhoto({
                              name: getUserName(entry.userId),
                              photoUrl: userNames[entry.userId].photoUrl!
                            });
                          }
                        }}
                      >
                        <div className="relative">
                          <ProfilePhoto
                            photoUrl={userNames[entry.userId]?.photoUrl}
                            name={getUserName(entry.userId)}
                            size="xs"
                          />
                          <span className="absolute -top-1 -left-1 w-4 h-4 rounded-full bg-amber-600 flex items-center justify-center text-[8px] font-bold text-white border border-gray-800">
                            {index + 1}
                          </span>
                        </div>
                        <span className="text-xs text-gray-300">
                          {entry.userId === currentUser?.id ? 'Tú' : getUserName(entry.userId).split(' ')[0]}
                        </span>
                      </div>
                    ))}
                    {selectedClass.waitlist.length > 5 && (
                      <div className="flex items-center justify-center px-3 py-1 bg-gray-800/50 rounded-full">
                        <span className="text-xs text-gray-400">
                          +{selectedClass.waitlist.length - 5} más
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* User waitlist position */}
              {isInWaitlist(selectedClass) && (
                <div className="bg-blue-900/30 border border-blue-700/50 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-400 text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Tu posición en lista de espera
                    </span>
                    <span className="text-blue-300 font-bold text-lg">
                      #{getWaitlistPosition(selectedClass)}
                    </span>
                  </div>
                  <p className="text-blue-400/70 text-xs mt-2">
                    Te avisaremos si se libera una plaza
                  </p>
                </div>
              )}

              {/* User credits info - Membresía y/o Bono */}
              {!isInWaitlist(selectedClass) && !isClassPast(selectedClass) && (userMembership || currentUser?.activePass) && (
                <div className="space-y-3 mb-6">
                  {/* Membresía */}
                  {userMembership?.isActive && userMembership.reservationsRemaining > 0 && (
                    <div className={`rounded-xl p-4 ${membershipAllowsClass(selectedClass)
                      ? 'bg-green-900/30 border border-green-700/50'
                      : 'bg-gray-800/50 border border-gray-700/50 opacity-60'
                      }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-green-400" />
                          <span className="text-green-400 text-sm">Tu membresía</span>
                          {membershipAllowsClass(selectedClass) && !passAllowsClass(selectedClass) && (
                            <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-300 rounded-full">Se usará</span>
                          )}
                        </div>
                        <span className="text-green-300 font-bold">
                          {userMembership.reservationsRemaining} clases restantes
                        </span>
                      </div>
                      {!membershipAllowsClass(selectedClass) && (
                        <p className="text-gray-500 text-xs mt-2">
                          ⚠️ Tu membresía no permite este tipo de clase o horario
                        </p>
                      )}
                    </div>
                  )}

                  {/* Bono */}
                  {currentUser?.activePass && currentUser.activePass.creditsRemaining > 0 && (
                    <div className={`rounded-xl p-4 ${passAllowsClass(selectedClass)
                      ? 'bg-amber-900/30 border border-amber-700/50'
                      : 'bg-gray-800/50 border border-gray-700/50 opacity-60'
                      }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-amber-400" />
                          <span className="text-amber-400 text-sm">
                            {currentUser.activePass.passName || 'Tu bono'}
                          </span>
                          {passAllowsClass(selectedClass) && !membershipAllowsClass(selectedClass) && (
                            <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded-full">Se usará</span>
                          )}
                        </div>
                        <span className="text-amber-300 font-bold">
                          {currentUser.activePass.creditsRemaining} clases restantes
                        </span>
                      </div>
                      {!passAllowsClass(selectedClass) && (
                        <p className="text-gray-500 text-xs mt-2">
                          ⚠️ Tu bono no permite este tipo de clase o horario
                        </p>
                      )}
                    </div>
                  )}

                  {/* Mensaje cuando ambos pueden usarse */}
                  {membershipAllowsClass(selectedClass) && passAllowsClass(selectedClass) && (
                    <p className="text-center text-gray-400 text-xs">
                      💡 Tienes ambos disponibles - podrás elegir cuál usar al reservar
                    </p>
                  )}
                </div>
              )}

              {/* Class Past Warning */}
              {isClassPast(selectedClass) && (
                <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-center gap-3">
                    <Clock className="w-5 h-5 text-gray-500" />
                    <p className="text-gray-400 text-sm font-medium">
                      Esta clase ya ha pasado
                    </p>
                  </div>
                </div>
              )}

              {/* Cancellation Warning */}
              {!isClassPast(selectedClass) && isUserEnrolled(selectedClass.id) && isWithinCancellationPenalty(selectedClass) && currentUser?.activePass && (
                <div className="bg-red-900/30 border border-red-700/50 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-400 text-sm font-medium">
                        ⚠️ Cancelación con penalización
                      </p>
                      <p className="text-red-400/70 text-xs mt-1">
                        Quedan menos de {cancellationHours}h para la clase.
                        Si cancelas ahora, <strong>no se devolverá el crédito</strong> de tu bono.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {isClassPast(selectedClass) ? (
                <div className="space-y-3">
                  <div className="w-full py-3 text-center text-gray-500 bg-gray-800/50 rounded-xl text-sm">
                    Esta clase ya ha finalizado
                  </div>
                  {canRateClass(selectedClass) && (
                    <Button
                      onClick={() => openRatingModal(selectedClass)}
                      className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold"
                    >
                      <Star className="w-5 h-5 mr-2" />
                      Valorar esta clase
                    </Button>
                  )}
                  {isUserEnrolled(selectedClass.id) && userRatings[selectedClass.id] && (
                    <div className="flex items-center justify-center gap-2 text-green-400 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      Ya has valorado esta clase
                    </div>
                  )}
                </div>
              ) : isUserEnrolled(selectedClass.id) ? (
                <Button
                  variant="outline"
                  onClick={() => cancelEnrollment(selectedClass.id)}
                  className="w-full py-4 text-red-400 border-red-500/50 hover:bg-red-900/20"
                >
                  {isWithinCancellationPenalty(selectedClass) && currentUser?.activePass
                    ? 'Cancelar (sin devolución)'
                    : 'Cancelar Reserva'
                  }
                </Button>
              ) : isInWaitlist(selectedClass) ? (
                <Button
                  variant="outline"
                  onClick={() => leaveWaitlist(selectedClass.id)}
                  className="w-full py-4 text-amber-400 border-amber-500/50 hover:bg-amber-900/20"
                >
                  Salir de Lista de Espera
                </Button>
              ) : (
                <Button
                  onClick={() => enrollInClass(selectedClass.id)}
                  className={`w-full py-4 font-bold text-lg ${getAvailableSpots(selectedClass) <= 0
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white'
                    : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white'
                    }`}
                >
                  {getAvailableSpots(selectedClass) <= 0 ? 'Unirse a Lista de Espera' : 'Reservar Plaza'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Profile Photo Modal */}
      {showProfilePhoto && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4"
          onClick={() => setShowProfilePhoto(null)}
        >
          <div className="relative max-w-md w-full">
            {/* Close button */}
            <button
              onClick={() => setShowProfilePhoto(null)}
              className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Photo */}
            <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={showProfilePhoto.photoUrl}
                alt={showProfilePhoto.name}
                className="w-full aspect-square object-cover"
              />
              <div className="p-4 text-center">
                <h3 className="text-xl font-bold text-white">{showProfilePhoto.name}</h3>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {showRatingModal && ratingClass && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4">
          <div className="bg-gray-900 rounded-2xl max-w-md w-full overflow-hidden border border-gray-800">
            {/* Header */}
            <div className="p-6 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Valorar Clase</h3>
                <button
                  onClick={() => setShowRatingModal(false)}
                  className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-gray-400 text-sm mt-1">{ratingClass.name}</p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Valorar Clase */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  ¿Cómo valorarías esta clase?
                </label>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      onClick={() => setClassRating(value)}
                      className={`p-2 rounded-lg transition-all ${classRating >= value
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-gray-800 text-gray-600 hover:text-gray-400'
                        }`}
                    >
                      <Star className={`w-8 h-8 ${classRating >= value ? 'fill-amber-400' : ''}`} />
                    </button>
                  ))}
                </div>
                <p className="text-center text-sm text-gray-500 mt-2">
                  {classRating === 0 ? 'Selecciona una puntuación' :
                    classRating === 1 ? 'Muy malo' :
                      classRating === 2 ? 'Malo' :
                        classRating === 3 ? 'Regular' :
                          classRating === 4 ? 'Bueno' : 'Excelente'}
                </p>
              </div>

              {/* Valorar Instructor */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  ¿Cómo valorarías al instructor?
                  <span className="block text-xs text-gray-500 font-normal mt-1">
                    {ratingClass.instructor}
                  </span>
                </label>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      onClick={() => setInstructorRating(value)}
                      className={`p-2 rounded-lg transition-all ${instructorRating >= value
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-gray-800 text-gray-600 hover:text-gray-400'
                        }`}
                    >
                      <Star className={`w-8 h-8 ${instructorRating >= value ? 'fill-amber-400' : ''}`} />
                    </button>
                  ))}
                </div>
                <p className="text-center text-sm text-gray-500 mt-2">
                  {instructorRating === 0 ? 'Selecciona una puntuación' :
                    instructorRating === 1 ? 'Muy malo' :
                      instructorRating === 2 ? 'Malo' :
                        instructorRating === 3 ? 'Regular' :
                          instructorRating === 4 ? 'Bueno' : 'Excelente'}
                </p>
              </div>

              {/* Info */}
              <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">
                  Tu valoración es anónima y ayuda a mejorar la calidad del servicio.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-gray-800">
              <Button
                onClick={submitRating}
                disabled={submittingRating || classRating === 0 || instructorRating === 0}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingRating ? 'Enviando...' : 'Enviar Valoración'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Credit Selection Modal - Cuando tiene bono y membresía */}
      {showCreditSelector && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4">
          <div className="bg-gray-900 rounded-2xl max-w-md w-full overflow-hidden border border-gray-800">
            {/* Header */}
            <div className="p-6 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">¿Qué quieres usar?</h3>
                <button
                  onClick={() => {
                    setShowCreditSelector(false);
                    setPendingEnrollClass(null);
                  }}
                  className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-gray-400 text-sm mt-1">
                Tienes créditos disponibles tanto en tu membresía como en tu bono
              </p>
            </div>

            {/* Options */}
            <div className="p-6 space-y-4">
              {/* Membresía */}
              {userMembership && userMembership.reservationsRemaining > 0 && (
                <button
                  onClick={() => handleCreditSelection('membership')}
                  className="w-full p-4 bg-gradient-to-r from-green-900/50 to-green-800/50 border border-green-600/50 rounded-xl hover:border-green-500 transition-all text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-green-600/30 flex items-center justify-center">
                        <Users className="w-6 h-6 text-green-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-white group-hover:text-green-400 transition-colors">
                          Membresía
                        </h4>
                        <p className="text-sm text-gray-400">
                          {userMembership.reservationsRemaining} clases restantes
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-green-400 transition-colors" />
                  </div>
                </button>
              )}

              {/* Bono */}
              {currentUser?.activePass && currentUser.activePass.creditsRemaining > 0 && (
                <button
                  onClick={() => handleCreditSelection('pass')}
                  className="w-full p-4 bg-gradient-to-r from-amber-900/50 to-orange-800/50 border border-amber-600/50 rounded-xl hover:border-amber-500 transition-all text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-amber-600/30 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-amber-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-white group-hover:text-amber-400 transition-colors">
                          Bono: {currentUser.activePass.passName}
                        </h4>
                        <p className="text-sm text-gray-400">
                          {currentUser.activePass.creditsRemaining} clases restantes
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-amber-400 transition-colors" />
                  </div>
                </button>
              )}
            </div>

            {/* Info */}
            <div className="px-6 pb-6">
              <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">
                  Se descontará 1 clase del crédito que elijas
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CalendarPage;
